import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { SITE } from "../helper/Static data/seo";

/**
 * Per-route <head> manager. Written by hand instead of react-helmet so the app
 * picks up no new dependency — every tag it writes is marked with
 * data-seo="1" and removed again when the page unmounts.
 */

const MANAGED = "data-seo";

/**
 * Superseded URL shapes, kept mounted only so old ?blog_id= / ?team_id= links
 * still land somewhere. The content itself lives at the slug URL, so these are
 * always noindex, nofollow regardless of what the page passes.
 */
const LEGACY_ROUTES = ["/our-team-view", "/blog-view"];

const upsertMeta = (attr, key, content) => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute(MANAGED, "1");
  el.setAttribute("content", content);
};

const upsertLink = (rel, href, extra = {}) => {
  if (!href) return;
  const selector = extra.hreflang
    ? `link[rel="${rel}"][hreflang="${extra.hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    Object.entries(extra).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  el.setAttribute(MANAGED, "1");
  el.setAttribute("href", href);
};

const clamp = (text, max) => {
  if (!text) return "";
  // API copy arrives as HTML; strip tags and entities before it lands in a meta tag.
  const plain = text
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/\s+/g, " ")
    .trim();
  if (plain.length <= max) return plain;
  return `${plain.slice(0, max - 1).replace(/[\s,;.-]+\S*$/, "")}…`;
};

export const Seo = ({
  title,
  description,
  image,
  imageAlt,
  path,
  type = "website",
  noindex = false,
  jsonLd,
}) => {
  const language_web = useSelector((state) => state.webLanguage.value);
  const location = useLocation();

  const pageTitle = title || SITE.defaultTitle;
  const pageDescription = clamp(description || SITE.defaultDescription, 160);
  const pagePath = path !== undefined ? path : location.pathname;
  const canonical = `${SITE.url}${pagePath === "/" ? "/" : pagePath}`;
  const ogImage = image ? (image.startsWith("http") ? image : `${SITE.url}${image}`) : SITE.ogImage;
  const isArabic = language_web === "ARE";
  const isLegacyRoute = LEGACY_ROUTES.includes(location.pathname);
  const structuredData = jsonLd ? JSON.stringify(jsonLd) : null;

  useEffect(() => {
    document.title = pageTitle;

    // The site is bilingual and the shell ships lang="en" hardcoded.
    document.documentElement.setAttribute("lang", isArabic ? "ar" : "en");
    document.documentElement.setAttribute("dir", isArabic ? "rtl" : "ltr");

    upsertMeta("name", "title", pageTitle);
    upsertMeta("name", "description", pageDescription);
    // Legacy URLs are a dead end on purpose. A soft-404 or an unlinked profile
    // is only unfit to *index* — Google should still follow its way back out to
    // /blog and /our-team, so those stay "follow".
    upsertMeta(
      "name",
      "robots",
      isLegacyRoute ? "noindex, nofollow" : noindex ? "noindex, follow" : "index, follow"
    );

    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:site_name", SITE.name);
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:title", pageTitle);
    upsertMeta("property", "og:description", pageDescription);
    upsertMeta("property", "og:image", ogImage);
    upsertMeta("property", "og:image:width", "1200");
    upsertMeta("property", "og:image:height", "630");
    upsertMeta("property", "og:image:alt", imageAlt || SITE.name);
    upsertMeta("property", "og:locale", isArabic ? "ar_AE" : "en_US");
    upsertMeta("property", "og:locale:alternate", isArabic ? "en_US" : "ar_AE");

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:url", canonical);
    upsertMeta("name", "twitter:title", pageTitle);
    upsertMeta("name", "twitter:description", pageDescription);
    upsertMeta("name", "twitter:image", ogImage);
    upsertMeta("name", "twitter:image:alt", imageAlt || SITE.name);

    upsertLink("canonical", canonical);
    upsertLink("alternate", canonical, { hreflang: "en" });
    upsertLink("alternate", canonical, { hreflang: "ar" });
    upsertLink("alternate", canonical, { hreflang: "x-default" });

    let ldTag = null;
    if (structuredData) {
      ldTag = document.createElement("script");
      ldTag.type = "application/ld+json";
      ldTag.setAttribute(MANAGED, "1");
      ldTag.text = structuredData;
      document.head.appendChild(ldTag);
    }

    return () => {
      if (ldTag && ldTag.parentNode) ldTag.parentNode.removeChild(ldTag);
    };
  }, [
    pageTitle,
    pageDescription,
    canonical,
    ogImage,
    imageAlt,
    type,
    noindex,
    isLegacyRoute,
    isArabic,
    structuredData,
  ]);

  return null;
};

export default Seo;
